import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Row, Col, Modal, Spinner } from 'react-bootstrap'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FaFileAlt, FaSearch, FaEye, FaArrowRight,
  FaMoneyBillWave, FaCalendarAlt, FaCheckCircle,
  FaTimesCircle, FaHourglass, FaRoute,
} from 'react-icons/fa'
import { fetchAllProposals } from '../../store/slices/transportRequestSlice'
import './TransportRequest.scss'

const fade = {
  hidden  : { opacity: 0, y: 12 },
  visible : i => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.32, ease: 'easeOut' } }),
}

const STATUS_MAP = {
  draft             : { mod: 'gray',   label: 'Draft'          },
  submitted         : { mod: 'yellow', label: 'Submitted'      },
  under_review      : { mod: 'blue',   label: 'Under Review'   },
  counter_proposed  : { mod: 'purple', label: 'Counter Bid'    },
  accepted          : { mod: 'green',  label: 'Accepted'       },
  rejected          : { mod: 'red',    label: 'Rejected'       },
}

const AllProposals = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { allProposals = [], loading } = useSelector(s => s.transportRequest)

  const [search,           setSearch]           = useState('')
  const [statusFilter,     setStatusFilter]     = useState('all')
  const [selectedProposal, setSelectedProposal] = useState(null)
  const [detailsModal,     setDetailsModal]     = useState(false)

  useEffect(() => { dispatch(fetchAllProposals()) }, [dispatch])

  const filtered = allProposals.filter(p => {
    const q = search.toLowerCase()
    const matchSearch =
      p.proposal_number?.toLowerCase().includes(q) ||
      p.request_number?.toLowerCase().includes(q)
    const matchStatus = statusFilter === 'all' || p.status === statusFilter
    return matchSearch && matchStatus
  })

  const total    = allProposals.length
  const accepted = allProposals.filter(p => p.status === 'accepted').length
  const pending  = allProposals.filter(p => !['accepted', 'rejected'].includes(p.status)).length
  const rejected = allProposals.filter(p => p.status === 'rejected').length

  const tabs = [
    { key: 'all',              label: 'All'          },
    { key: 'submitted',        label: 'Submitted'    },
    { key: 'under_review',     label: 'Under Review' },
    { key: 'counter_proposed', label: 'Counter Bid'  },
    { key: 'accepted',         label: 'Accepted'     },
    { key: 'rejected',         label: 'Rejected'     },
  ]

  const openDetails = p => { setSelectedProposal(p); setDetailsModal(true) }
  const closeDetails = () => { setDetailsModal(false); setSelectedProposal(null) }

  return (
    <div className="trl-page">

      {/* Header */}
      <motion.div initial="hidden" animate="visible" variants={fade} custom={0} className="trl-header">
        <div className="trl-header-left">
          <div className="trl-header-icon"><FaFileAlt /></div>
          <div>
            <h4 className="trl-title">All My Proposals</h4>
            <p className="trl-sub">View and manage all your submitted freight proposals</p>
          </div>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <Row className="g-3 mb-3">
        {[
          { label: 'Total Proposals', val: total,    mod: 'blue',   icon: <FaFileAlt />,      note: 'All proposals'       },
          { label: 'Accepted',        val: accepted, mod: 'green',  icon: <FaCheckCircle />,  note: 'Deals confirmed'     },
          { label: 'In Progress',     val: pending,  mod: 'yellow', icon: <FaHourglass />,    note: 'Awaiting response'   },
          { label: 'Rejected',        val: rejected, mod: 'red',    icon: <FaTimesCircle />,  note: 'Not selected'        },
        ].map((k, i) => (
          <Col xs={12} md={3} sm={6} key={k.label}>
            <motion.div initial="hidden" animate="visible" variants={fade} custom={i + 1} className={`trl-kpi trl-kpi--${k.mod}`}>
              <div className="trl-kpi-left">
                <span className="trl-kpi-val">{k.val}</span>
                <span className="trl-kpi-label">{k.label}</span>
                <span className="trl-kpi-note">{k.note}</span>
              </div>
              <span className="trl-kpi-icon">{k.icon}</span>
            </motion.div>
          </Col>
        ))}
      </Row>

      {/* Filter Bar */}
      <motion.div initial="hidden" animate="visible" variants={fade} custom={5} className="trl-filter-bar">
        <div className="trl-search-wrap">
          <FaSearch className="trl-search-icon" />
          <input className="trl-search" placeholder="Search proposal or request ID…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="trl-filter-tabs">
          {tabs.map(t => (
            <button key={t.key} className={`trl-tab${statusFilter === t.key ? ' trl-tab--active' : ''}`} onClick={() => setStatusFilter(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        <span className="trl-count">{filtered.length} proposal{filtered.length !== 1 ? 's' : ''}</span>
      </motion.div>

      {/* List */}
      {loading ? (
        <div className="trl-loading">
          <Spinner animation="border" size="sm" style={{ color: 'rgb(20,35,115)' }} />
          <span>Loading proposals…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="trl-empty">
          <div className="trl-empty-icon"><FaFileAlt /></div>
          <p className="trl-empty-title">{search || statusFilter !== 'all' ? 'No matching proposals' : 'No proposals yet'}</p>
          <p className="trl-empty-sub">{search || statusFilter !== 'all' ? 'Try adjusting your search or filters.' : 'Submit a proposal on a transport request to see it here.'}</p>
        </div>
      ) : (
        <Row className="g-3">
          <AnimatePresence>
            {filtered.map((p, i) => {
              const st      = STATUS_MAP[p.status] || { mod: 'gray', label: p.status || '—' }
              const subDate = p.submitted_at
                ? new Date(p.submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                : '—'

              return (
                <Col xs={12} sm={6} md={4} xl={3} key={p.id}>
                  <motion.div
                    className="trl-card"
                    initial="hidden" animate="visible" exit={{ opacity: 0, y: -8 }}
                    variants={fade} custom={i}
                    layout
                  >
                    <div className="trl-card-accent" />
                    <div className="trl-card-head">
                      <div className="trl-card-meta">
                        <span className="trl-card-number">{p.proposal_number}</span>
                        <span className={`trl-badge trl-badge--${st.mod}`}>{st.label}</span>
                      </div>
                    </div>

                    <div className="trl-route">
                      <div className="trl-route-row" style={{ gap: 6 }}>
                        <FaRoute className="trl-card-icon" style={{ fontSize: '0.68rem' }} />
                        <span className="trl-route-place">{p.origin}</span>
                        <FaArrowRight style={{ fontSize: '0.6rem', color: '#a0aec0', flexShrink: 0 }} />
                        <span className="trl-route-place">{p.destination}</span>
                      </div>
                    </div>

                    <div className="trl-card-details">
                      <div className="trl-card-row">
                        <FaMoneyBillWave className="trl-card-icon" />
                        <span className="trl-card-val">₹ {parseFloat(p.transport_rate || 0).toLocaleString()} / {p.rate_unit || 'unit'}</span>
                      </div>
                      <div className="trl-card-row">
                        <FaMoneyBillWave className="trl-card-icon" />
                        <span className="trl-card-val" style={{ fontWeight: 700, color: '#1a202c' }}>Total: ₹ {parseFloat(p.total_freight_cost || 0).toLocaleString()}</span>
                      </div>
                      <div className="trl-card-row">
                        <FaCalendarAlt className="trl-card-icon" />
                        <span className="trl-card-val">{subDate}</span>
                      </div>
                    </div>

                    <div className="trl-card-footer" style={{ gap: 6 }}>
                      <button className="trl-card-btn trl-card-btn--view" onClick={() => openDetails(p)}>
                        <FaEye style={{ fontSize: '0.68rem' }} /> Details
                      </button>
                      <button className="trl-card-btn trl-card-btn--view" style={{ flex: 'unset', padding: '5px 10px' }} onClick={() => navigate(`/transport-request/${p.request_id}`)}>
                        Request
                      </button>
                    </div>
                  </motion.div>
                </Col>
              )
            })}
          </AnimatePresence>
        </Row>
      )}

      {/* Details Modal */}
      <Modal show={detailsModal} onHide={closeDetails} centered size="lg">
        <Modal.Header closeButton className="trl-modal-header">
          <Modal.Title className="trl-modal-title">Proposal Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="trl-modal-body">
          {selectedProposal && (
            <Row className="g-3">
              {[
                { label: 'Proposal No.',  val: selectedProposal.proposal_number },
                { label: 'Request No.',   val: selectedProposal.request_number  },
                { label: 'Status',        val: <span className={`trl-badge trl-badge--${(STATUS_MAP[selectedProposal.status] || {}).mod || 'gray'}`}>{(STATUS_MAP[selectedProposal.status] || { label: selectedProposal.status }).label}</span> },
                { label: 'Route',         val: `${selectedProposal.origin} → ${selectedProposal.destination}` },
                { label: 'Transport Rate',val: `₹ ${parseFloat(selectedProposal.transport_rate || 0).toLocaleString()} / ${selectedProposal.rate_unit}` },
                { label: 'Total Freight', val: `₹ ${parseFloat(selectedProposal.total_freight_cost || 0).toLocaleString()}` },
                { label: 'Payment Terms', val: selectedProposal.payment_terms || 'Standard terms apply' },
              ].map(row => (
                <Col xs={12} md={6} key={row.label}>
                  <div className="trl-view-row">
                    <div className="trl-view-lbl">{row.label}</div>
                    <div className="trl-view-val">{row.val}</div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className="trl-modal-footer">
          <button className="trl-btn-cancel" onClick={closeDetails}>Close</button>
          <button className="trl-btn-submit" onClick={() => { closeDetails(); navigate(`/transport-request/${selectedProposal?.request_id}`) }}>
            View Request
          </button>
        </Modal.Footer>
      </Modal>

    </div>
  )
}

export default AllProposals
